import React, { useEffect, useState } from 'react';
import styles from './ExtraWorkingTime.module.scss';
import { useDispatch, useSelector } from 'react-redux';
import { getWorker, getWorkerExtraWorkingTime } from '../../../../store/selectors';
import { salonManagementActions } from '../../../../store';
import { useNavigate, useParams } from 'react-router-dom';
import { getActiveSalonId } from '../../../../../Auth/store/selectors';
import TitleSectionBack from '../../../../../Common/TitleSectionBack';
import AvatarIcon from '../../../../../Common/AvatarIcon';
import { modalActions } from '../../../../../Modals/store';
import clx from 'classnames';
import useWindowSize from '../../../../../../utils/useWindowSize';
import ConfirmDeleteModal from '../../../../../Common/ConfirmDeleteModal';
import CustomDatePicker from '../../../../../Common/CustomDatePicker';
import dayjs from 'dayjs';
import 'dayjs/locale/it';
import { useTranslation } from 'react-i18next';
import { CalendarPlus, Clock3, Plus, Trash2 } from 'lucide-react';

dayjs.locale('it');

const formatHm = (value) => dayjs(value, 'HH:mm:ss').format('HH:mm');

const getScheduleLines = (item, t) => {
  const morningStart = formatHm(item?.oraInizioMattina);
  const morningEnd = formatHm(item?.oraFineMattina);
  const eveningStart = formatHm(item?.oraInizioPomeriggio);
  const eveningEnd = formatHm(item?.oraFinePomeriggio);
  const isFullDay =
    item?.oraFineMattina === '00:00:00' && item?.oraInizioPomeriggio === '00:00:00';

  if (isFullDay) {
    return [
      `${t('workers.extraWorkingTime.card.fromTime')} ${morningStart} ${t('workers.extraWorkingTime.card.toTime')} ${eveningEnd}`,
    ];
  }

  return [
    `${t('workers.extraWorkingTime.card.fromTime')} ${morningStart} ${t('workers.extraWorkingTime.card.toTime')} ${morningEnd}`,
    `${t('workers.extraWorkingTime.card.fromTime')} ${eveningStart} ${t('workers.extraWorkingTime.card.toTime')} ${eveningEnd}`,
  ];
};

const ExtraWorkingTime = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { workerId } = useParams();
  const navigate = useNavigate();
  const worker = useSelector(getWorker());
  const activeSalonId = useSelector(getActiveSalonId());
  const { isMobile } = useWindowSize();

  const [tablesFilterDate, setTablesFilterDate] = useState(null);
  const extraWorkingTime = useSelector(getWorkerExtraWorkingTime(tablesFilterDate)) || [];
  const [extraTimeIdToDelete, setExtraTimeIdToDelete] = useState(false);

  useEffect(() => {
    dispatch(salonManagementActions.GET_WORKER.REQUEST(workerId, null, { skipLoader: true }));
    return () => {
      dispatch(salonManagementActions.GET_WORKER.CLEAR());
    };
  }, [workerId]);

  useEffect(() => {
    if (worker?.salone && worker?.salone !== activeSalonId) {
      dispatch(salonManagementActions.GET_WORKER.CLEAR());
      navigate(-1);
    }
  }, [worker, activeSalonId]);

  const openCreateModal = () => {
    dispatch(
      modalActions.OPEN_MODAL.SUCCESS({
        type: 'workerExtraWorkingTimeModal',
        props: { worker: worker?.nome_referente },
      }),
    );
  };

  const addExtraButton = (
    <button type="button" className={styles.add_button} onClick={openCreateModal}>
      <Plus size={16} strokeWidth={2.4} aria-hidden />
      <span>{t('workers.extraWorkingTime.newExtraOpening')}</span>
    </button>
  );

  return (
    <>
      <ConfirmDeleteModal
        isModalOpen={extraTimeIdToDelete}
        setIsModalOpen={setExtraTimeIdToDelete}
        title={t('workers.extraWorkingTime.confirmDelete.title')}
        buttons={[t('common.actions.cancel'), t('common.actions.disable')]}
        action={() =>
          dispatch(
            salonManagementActions.DELETE_WORKER_EXTRA_TIME.REQUEST(
              {
                idPoltronaOrari: extraTimeIdToDelete,
              },
              () => setExtraTimeIdToDelete(false),
            ),
          )
        }
      />

      <div className={clx(styles.page, { [styles.page_mobile]: isMobile })}>
        <div className={styles.top_bar}>
          <TitleSectionBack
            title={t('workers.extraWorkingTime.title')}
            description={t('workers.extraWorkingTime.description')}
          />
          {!isMobile ? (
            <div className={styles.top_actions}>
              <div className={styles.worker_chip}>
                <AvatarIcon name={worker?.nome_referente} icon={worker?.icon} size="40px" />
                <div className={styles.worker_chip_copy}>
                  <span>{t('workers.extraWorkingTime.modifyingSchedule')}</span>
                  <strong>{worker?.nome_referente || '—'}</strong>
                </div>
              </div>
              {addExtraButton}
            </div>
          ) : null}
        </div>

        {isMobile ? (
          <div className={styles.mobile_toolbar}>
            <div className={styles.worker_chip}>
              <AvatarIcon name={worker?.nome_referente} icon={worker?.icon} size="40px" />
              <div className={styles.worker_chip_copy}>
                <span>{t('workers.extraWorkingTime.modifyingSchedule')}</span>
                <strong>{worker?.nome_referente || '—'}</strong>
              </div>
            </div>
            {addExtraButton}
          </div>
        ) : null}

        <div className={styles.toolbar}>
          <div className={styles.toolbar_copy}>
            <h3>{t('workers.extraWorkingTime.listTitle')}</h3>
            <span>
              {extraWorkingTime.length}{' '}
              {extraWorkingTime.length === 1
                ? t('workers.extraWorkingTime.countOne')
                : t('workers.extraWorkingTime.countMany')}
            </span>
          </div>
          <div className={styles.picker_wrap}>
            <CustomDatePicker
              type="button"
              value={tablesFilterDate}
              setValue={setTablesFilterDate}
              placeholder={t('workers.extraWorkingTime.filterByDate')}
              disablePast
            />
          </div>
        </div>

        {extraWorkingTime.length ? (
          <div className={styles.cards_grid}>
            {extraWorkingTime.map((item) => {
              const sameDay = item.dataInizio === item.dataFine;
              const dayLabel = dayjs().day(item.giorno).format('dddd');
              const scheduleLines = getScheduleLines(item, t);

              return (
                <article key={item.idPoltronaOrari} className={styles.card}>
                  <div className={styles.card_icon} aria-hidden>
                    <CalendarPlus size={18} strokeWidth={2.2} />
                  </div>
                  <div className={styles.card_main}>
                    <div className={styles.card_title_row}>
                      <h4>
                        {sameDay
                          ? dayjs(item.dataInizio).format('DD MMM YYYY')
                          : `${dayjs(item.dataInizio).format('DD MMM')} – ${dayjs(item.dataFine).format('DD MMM YYYY')}`}
                      </h4>
                      <span
                        className={clx(styles.badge, {
                          [styles.badge_pay]: !!item.mustPay,
                        })}
                      >
                        {item.mustPay
                          ? t('workers.extraWorkingTime.card.active')
                          : t('workers.extraWorkingTime.card.notActive')}
                      </span>
                    </div>
                    <div className={styles.meta}>
                      <span className={clx(styles.pill, styles.pill_day)}>
                        {dayLabel.charAt(0).toUpperCase() + dayLabel.slice(1)}
                      </span>
                      {scheduleLines.map((line) => (
                        <span key={line} className={clx(styles.pill, styles.pill_muted)}>
                          <Clock3 size={12} strokeWidth={2.4} aria-hidden />
                          {line}
                        </span>
                      ))}
                    </div>
                  </div>
                  <button
                    type="button"
                    className={styles.delete_btn}
                    onClick={() => setExtraTimeIdToDelete(item.idPoltronaOrari)}
                  >
                    <Trash2 size={15} strokeWidth={2.3} aria-hidden />
                    {t('workers.extraWorkingTime.card.deleteOpening')}
                  </button>
                </article>
              );
            })}
          </div>
        ) : (
          <div className={styles.empty_state}>
            <p>{t('workers.extraWorkingTime.noExtraOpenings')}</p>
            {addExtraButton}
          </div>
        )}
      </div>
    </>
  );
};

export default ExtraWorkingTime;
